import React, { useState } from 'react';
import { Button, FormField, Select } from './ui';
import { COUNTRY_OPTIONS, findCountry } from '../constants/countries';
import './InstagramRegionField.css';

type Props = {
  /** ISO-3166 alpha-2, uppercase. */
  value: string;
  onChange: (_code: string) => void;
  idPrefix?: string;
  disabled?: boolean;
};

/**
 * The publishing region for one Instagram account.
 *
 * This is the only country the product asks for, and it is not a preference:
 * it picks the residential exit node every login and publish for the account
 * travels through, and the locale and timezone we report to Instagram are
 * derived from it (see backend/scheduler/instagram_locale.py). Getting it wrong
 * is the mismatch Meta's integrity model scores, and the cost lands on the
 * customer as a challenge and a manual re-auth.
 *
 * Presented as a confirmed default rather than a question: the caller resolves
 * a sensible region up front (`resolveDefaultCountry`), this states the answer
 * in plain language, and changing it is one click. It used to be "Step 1" of
 * the wizard — the least-informed decision demanded before the user had typed
 * their handle. See docs/instagram-connection-2026.md.
 */
export function InstagramRegionField({
  value,
  onChange,
  idPrefix = 'ig-region',
  disabled = false,
}: Props) {
  const selected = findCountry(value);
  // Open by default only when the stored value is one we cannot route, so the
  // user is never left looking at a region that will silently become the US.
  const [editing, setEditing] = useState(!selected);

  if (!editing && selected) {
    return (
      <div className="ig-region">
        <div className="ig-region__row">
          <span className="ig-region__label">Publishing region</span>
          <span className="ig-region__value">
            <span className="ig-region__flag" aria-hidden="true">
              {selected.flag}
            </span>
            {selected.label}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => setEditing(true)}
          >
            Change
          </Button>
        </div>
        <p className="ig-region__hint">
          Posts are published from an IP address in {selected.label}, and we tell
          Instagram this account is in {selected.label}.
        </p>
      </div>
    );
  }

  return (
    <div className="ig-region ig-region--editing">
      <FormField
        label="Publishing region"
        htmlFor={`${idPrefix}-select`}
        hint="Pick the country this Instagram account normally logs in from. Posts are published from an IP address there, so matching it keeps the account looking consistent to Instagram."
      >
        <Select
          id={`${idPrefix}-select`}
          value={selected ? selected.value : ''}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            if (e.target.value) setEditing(false);
          }}
        >
          {!selected && (
            <option value="" disabled>
              Select a region…
            </option>
          )}
          {COUNTRY_OPTIONS.map((country) => (
            <option key={country.value} value={country.value}>
              {country.flag} {country.label}
            </option>
          ))}
        </Select>
      </FormField>
    </div>
  );
}
